import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api } from "../api";
import { useAppStore } from "../app/store";
import { useProjectHint } from "./projectHint";
import type {
  Checklist,
  ClarifyDecision,
  Horizon,
  HorizonInput,
  HorizonStatus,
  InboxItem,
  Page,
  ProjectCreateInput,
  ProjectInput,
  ProjectStatus,
  ProjectView,
  ReferenceFilters,
  ReferenceInput,
  Task,
  TaskFilters,
  TaskInput,
} from "./types";

/** `all` is the UI's "no filter"; the API wants the parameter omitted. */
export function useSpaceParam(): string | undefined {
  const space = useAppStore((s) => s.space);
  return space === "all" ? undefined : space;
}

export const keys = {
  spaces: ["spaces"] as const,
  inbox: ["inbox"] as const,
  tasks: (filters: TaskFilters) => ["tasks", filters] as QueryKey,
  projects: (space?: string, status?: ProjectStatus) =>
    (status ? ["projects", space ?? "all", status] : ["projects", space ?? "all"]) as QueryKey,
  projectTasks: (id: string) => ["projects", id, "tasks"] as QueryKey,
  checklists: ["checklists"] as const,
  references: (filters: ReferenceFilters) => ["references", filters] as QueryKey,
  horizons: (space?: string, status?: HorizonStatus[]) =>
    ["horizons", space ?? "all", status ?? "active"] as QueryKey,
  horizon: (id: string) => ["horizons", "detail", id] as QueryKey,
};

/** Everything a task mutation can invalidate (lists overlap heavily). */
function invalidateTaskViews(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["tasks"] });
  void qc.invalidateQueries({ queryKey: ["projects"] });
  // Licznik projektów przy obszarze i celu zależy od każdej zmiany projektu.
  void qc.invalidateQueries({ queryKey: ["horizons"] });
}

// --------------------------------------------------------------------------
// Spaces
// --------------------------------------------------------------------------

export function useSpaces() {
  return useQuery({ queryKey: keys.spaces, queryFn: api.spaces, staleTime: 5 * 60_000 });
}

// --------------------------------------------------------------------------
// Inbox
// --------------------------------------------------------------------------

export function useInbox() {
  return useQuery({ queryKey: keys.inbox, queryFn: () => api.inbox({ limit: 100 }) });
}

export function useCapture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => api.capture(text),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.inbox }),
  });
}

export function useClarify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stuffId, decision }: { stuffId: string; decision: ClarifyDecision }) =>
      api.clarify(stuffId, decision),
    onMutate: async ({ stuffId }) => {
      await qc.cancelQueries({ queryKey: keys.inbox });
      const previous = qc.getQueryData<Page<InboxItem>>(keys.inbox);
      qc.setQueryData<Page<InboxItem>>(keys.inbox, (old) =>
        old
          ? {
              ...old,
              items: old.items.filter((i) => i.stuff.id !== stuffId),
              total: Math.max(old.total - 1, 0),
            }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.inbox, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.inbox });
      void qc.invalidateQueries({ queryKey: keys.checklists });
      void qc.invalidateQueries({ queryKey: ["references"] });
      invalidateTaskViews(qc);
    },
  });
}

export function useTrashStuff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stuffId: string) => api.trashStuff(stuffId),
    onMutate: async (stuffId) => {
      await qc.cancelQueries({ queryKey: keys.inbox });
      const previous = qc.getQueryData<Page<InboxItem>>(keys.inbox);
      qc.setQueryData<Page<InboxItem>>(keys.inbox, (old) =>
        old
          ? {
              ...old,
              items: old.items.filter((i) => i.stuff.id !== stuffId),
              total: Math.max(old.total - 1, 0),
            }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.inbox, ctx.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.inbox }),
  });
}

// --------------------------------------------------------------------------
// Tasks
// --------------------------------------------------------------------------

export function useTasks(filters: TaskFilters) {
  return useQuery({ queryKey: keys.tasks(filters), queryFn: () => api.tasks(filters) });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaskInput) => api.createTask(input),
    onSuccess: () => invalidateTaskViews(qc),
  });
}

/** Optimistically drops the task from every cached task list. */
function removeTaskFromLists(qc: ReturnType<typeof useQueryClient>, taskId: string) {
  const snapshots = qc.getQueriesData<Page<Task>>({ queryKey: ["tasks"] });
  for (const [key, data] of snapshots) {
    if (!data) continue;
    qc.setQueryData<Page<Task>>(key, {
      ...data,
      items: data.items.filter((t) => t.id !== taskId),
      total: Math.max(data.total - 1, 0),
    });
  }
  return snapshots;
}

function restore(qc: ReturnType<typeof useQueryClient>, snapshots: [QueryKey, unknown][]) {
  for (const [key, data] of snapshots) qc.setQueryData(key, data);
}

export function useCompleteTask() {
  const qc = useQueryClient();
  const showHint = useProjectHint((s) => s.show);
  return useMutation({
    mutationFn: (taskId: string) => api.completeTask(taskId),
    onMutate: async (taskId) => {
      await qc.cancelQueries({ queryKey: ["tasks"] });
      return { snapshots: removeTaskFromLists(qc, taskId) };
    },
    onSuccess: (task) => {
      if (task.project_needs_next_action) showHint(task.project_needs_next_action);
    },
    onError: (_err, _vars, ctx) => ctx && restore(qc, ctx.snapshots),
    onSettled: () => invalidateTaskViews(qc),
  });
}

export function useTrashTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.trashTask(taskId),
    onMutate: async (taskId) => {
      await qc.cancelQueries({ queryKey: ["tasks"] });
      return { snapshots: removeTaskFromLists(qc, taskId) };
    },
    onError: (_err, _vars, ctx) => ctx && restore(qc, ctx.snapshots),
    onSettled: () => invalidateTaskViews(qc),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<TaskInput> }) =>
      api.updateTask(id, input),
    onSettled: () => invalidateTaskViews(qc),
  });
}

// --------------------------------------------------------------------------
// Projects
// --------------------------------------------------------------------------

/** Bez `status` serwer zwraca aktywne. */
export function useProjects(space?: string, status?: ProjectStatus) {
  return useQuery<ProjectView[]>({
    queryKey: keys.projects(space, status),
    queryFn: () => api.projects({ space, status: status ? [status] : undefined }),
  });
}

export function useProjectTasks(projectId: string | null) {
  return useQuery({
    queryKey: keys.projectTasks(projectId ?? "none"),
    queryFn: () => api.projectTasks(projectId as string),
    enabled: Boolean(projectId),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectCreateInput) => api.createProject(input),
    onSuccess: () => invalidateTaskViews(qc),
  });
}

export function useAddProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, input }: { projectId: string; input: TaskInput }) =>
      api.addProjectTask(projectId, input),
    onSuccess: (_task, { projectId }) => {
      void qc.invalidateQueries({ queryKey: keys.projectTasks(projectId) });
      invalidateTaskViews(qc);
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ProjectInput }) =>
      api.updateProject(id, input),
    onSuccess: () => invalidateTaskViews(qc),
  });
}

export function useCompleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => api.completeProject(projectId),
    onSuccess: () => invalidateTaskViews(qc),
  });
}

/** Completing a task inside the project drawer must refresh that drawer too. */
export function useCompleteProjectTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.completeTask(taskId),
    onMutate: async (taskId) => {
      const key = keys.projectTasks(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Page<Task>>(key);
      qc.setQueryData<Page<Task>>(key, (old) =>
        old
          ? {
              ...old,
              items: old.items.map((t) =>
                t.id === taskId ? { ...t, status: "done", completed_at: new Date().toISOString() } : t,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.projectTasks(projectId), ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.projectTasks(projectId) });
      invalidateTaskViews(qc);
    },
  });
}

// --------------------------------------------------------------------------
// Checklists
// --------------------------------------------------------------------------

export function useChecklists() {
  return useQuery({ queryKey: keys.checklists, queryFn: api.checklists });
}

function patchChecklists(
  qc: ReturnType<typeof useQueryClient>,
  update: (lists: Checklist[]) => Checklist[],
) {
  const previous = qc.getQueryData<Checklist[]>(keys.checklists);
  if (previous) qc.setQueryData<Checklist[]>(keys.checklists, update(previous));
  return previous;
}

export function useToggleChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      checklistId,
      itemId,
      checked,
    }: {
      checklistId: string;
      itemId: string;
      checked: boolean;
    }) => api.setChecklistItem(checklistId, itemId, { checked }),
    onMutate: async ({ checklistId, itemId, checked }) => {
      await qc.cancelQueries({ queryKey: keys.checklists });
      const previous = patchChecklists(qc, (lists) =>
        lists.map((list) =>
          list.id !== checklistId
            ? list
            : {
                ...list,
                items: list.items.map((i) => (i.id === itemId ? { ...i, checked } : i)),
              },
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.checklists, ctx.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.checklists }),
  });
}

export function useAddChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ checklistId, text }: { checklistId: string; text: string }) =>
      api.addChecklistItem(checklistId, text),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.checklists }),
  });
}

export function useRemoveChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ checklistId, itemId }: { checklistId: string; itemId: string }) =>
      api.removeChecklistItem(checklistId, itemId),
    onMutate: async ({ checklistId, itemId }) => {
      await qc.cancelQueries({ queryKey: keys.checklists });
      const previous = patchChecklists(qc, (lists) =>
        lists.map((list) =>
          list.id !== checklistId
            ? list
            : { ...list, items: list.items.filter((i) => i.id !== itemId) },
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.checklists, ctx.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.checklists }),
  });
}

export function useClearChecked() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (checklistId: string) => api.clearChecked(checklistId),
    onMutate: async (checklistId) => {
      await qc.cancelQueries({ queryKey: keys.checklists });
      const previous = patchChecklists(qc, (lists) =>
        lists.map((list) =>
          list.id !== checklistId ? list : { ...list, items: list.items.filter((i) => !i.checked) },
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.checklists, ctx.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.checklists }),
  });
}

export function useCreateChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createChecklist(name),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.checklists }),
  });
}

export function useDeleteChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (checklistId: string) => api.deleteChecklist(checklistId),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.checklists }),
  });
}

// --------------------------------------------------------------------------
// Materiały referencyjne
// --------------------------------------------------------------------------

export function useReferences(filters: ReferenceFilters, enabled = true) {
  return useQuery({
    queryKey: keys.references(filters),
    queryFn: () => api.references(filters),
    enabled,
  });
}

function useReferenceMutation<TInput>(mutationFn: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => void qc.invalidateQueries({ queryKey: ["references"] }),
  });
}

export function useCreateReference() {
  return useReferenceMutation((input: ReferenceInput) => api.createReference(input));
}

export function useUpdateReference() {
  return useReferenceMutation(({ id, input }: { id: string; input: ReferenceInput }) =>
    api.updateReference(id, input),
  );
}

export function useHorizons(space?: string, status?: HorizonStatus[]) {
  return useQuery<Horizon[]>({
    queryKey: keys.horizons(space, status),
    queryFn: () => api.horizons({ space, status }),
  });
}

export function useHorizonDetail(id: string | null) {
  return useQuery({
    queryKey: keys.horizon(id ?? "none"),
    queryFn: () => api.horizon(id as string),
    enabled: Boolean(id),
  });
}

function useHorizonMutation<TInput>(mutationFn: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["horizons"] }),
  });
}

export function useCreateHorizon() {
  return useHorizonMutation((input: HorizonInput) => api.createHorizon(input));
}

export function useUpdateHorizon() {
  return useHorizonMutation(({ id, input }: { id: string; input: HorizonInput }) =>
    api.updateHorizon(id, input),
  );
}

export function useTrashReference() {
  return useReferenceMutation((id: string) => api.trashReference(id));
}

export function useRestoreReference() {
  return useReferenceMutation((id: string) => api.restoreReference(id));
}
